import { Link } from "react-router-dom";
import { Package, ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "September 1, 2026";
const CONTACT_EMAIL = "lovegodandhatesatan@gmail.com";

export default function PrivacyPolicyPage() {
    return (
        <div className="min-h-screen bg-[#F4F4F5]">
            {/* Nav */}
            <header className="border-b-2 border-zinc-900 bg-white">
                <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
                            <Package size={22} weight="bold" color="#FFCC00" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-display text-lg font-extrabold tracking-tight">
                                QuoteMatrix
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                Shipping calculator
                            </div>
                        </div>
                    </Link>
                    <Link
                        to="/"
                        className="border-2 border-zinc-900 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-zinc-900 hover:text-white transition-colors inline-flex items-center gap-2"
                    >
                        <ArrowLeft size={12} weight="bold" />
                        Back to home
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-12">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-2">
                    Legal
                </div>
                <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">
                    Privacy Policy
                </h1>
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                    Effective date · {EFFECTIVE_DATE}
                </p>

                <div className="mt-10 space-y-8 text-zinc-800 leading-relaxed text-[15px]">
                    <section>
                        <p>
                            This Privacy Policy explains how QuoteMatrix
                            (&quot;we&quot;, &quot;us&quot;) handles information
                            when you use the QuoteMatrix website at{" "}
                            <a
                                href="https://quotematrix.online"
                                className="underline font-bold"
                            >
                                quotematrix.online
                            </a>{" "}
                            and the <strong>QuoteMatrix Quick-Scan</strong>{" "}
                            browser extension for Chrome, Edge and Brave.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            1. What the Quick-Scan extension does
                        </h2>
                        <p>
                            The QuoteMatrix Quick-Scan extension has a{" "}
                            <strong>single purpose</strong>: to let you send the
                            URL of the product page you are currently viewing
                            (for example, on Amazon, eBay, Walmart, Shein or
                            AliExpress) to QuoteMatrix so we can automatically
                            estimate the landed cost in Guyanese dollars (GYD).
                        </p>
                        <p className="mt-3">
                            The extension only sends the current
                            product-page URL <strong>after you actively click</strong>{" "}
                            the &quot;Send to QuoteMatrix&quot; button that the
                            extension injects on supported product pages, or the
                            QuoteMatrix toolbar icon. No data is transmitted
                            when you are simply browsing.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            2. Information collected
                        </h2>
                        <p>
                            When you tap the &quot;Send to QuoteMatrix&quot;
                            button, the extension transmits only the following
                            to QuoteMatrix:
                        </p>
                        <ul className="mt-3 space-y-2 list-disc pl-6">
                            <li>
                                The full URL of the product page you were
                                viewing.
                            </li>
                        </ul>
                        <p className="mt-3">
                            QuoteMatrix then fetches the public content of that
                            URL through our server-side scraper and uses AI to
                            extract the product&apos;s price, currency, and
                            weight so we can display an estimated GYD total.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            3. Information we do NOT collect
                        </h2>
                        <p>The Quick-Scan extension does not collect:</p>
                        <ul className="mt-3 space-y-2 list-disc pl-6">
                            <li>
                                Passwords, PINs, security questions, or any
                                other authentication credentials
                            </li>
                            <li>
                                Credit-card numbers, bank details, or any
                                payment-card information
                            </li>
                            <li>Health, medical, or biometric information</li>
                            <li>
                                Personal communications (emails, chats, DMs,
                                SMS)
                            </li>
                            <li>
                                Browsing history, form data, keystrokes,
                                cookies, or web-activity beyond the single URL
                                you explicitly send
                            </li>
                            <li>
                                Location data, contacts, calendar, files or any
                                personally identifiable information
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            4. How we use the information
                        </h2>
                        <p>The URL you send is used solely to:</p>
                        <ul className="mt-3 space-y-2 list-disc pl-6">
                            <li>
                                Load the product page server-side and extract
                                its price and weight
                            </li>
                            <li>Convert the price into GYD</li>
                            <li>
                                Show you a shipping-and-duties estimate on
                                quotematrix.online
                            </li>
                        </ul>
                        <p className="mt-3">
                            We do not use the URL for any other purpose. We do
                            not build a profile of your browsing habits.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            5. What we don&apos;t do with your data
                        </h2>
                        <ul className="mt-3 space-y-2 list-disc pl-6">
                            <li>
                                We do <strong>not</strong> sell, rent or trade
                                extension user data.
                            </li>
                            <li>
                                We do <strong>not</strong> use extension data
                                for advertising, ad targeting or ad networks.
                            </li>
                            <li>
                                We do <strong>not</strong> use extension data to
                                determine creditworthiness or for any lending
                                purpose.
                            </li>
                            <li>
                                We do <strong>not</strong> transfer extension
                                data to third parties for any purpose unrelated
                                to the extension&apos;s single purpose of
                                producing a shipping-cost estimate.
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            6. Remote code
                        </h2>
                        <p>
                            The extension does <strong>not</strong> load,
                            evaluate or execute any remote code. All JavaScript
                            and CSS the extension uses is included in the
                            extension package published to the Chrome Web Store
                            and is available for inspection. The extension
                            simply opens a link to quotematrix.online in a new
                            tab — all subsequent price extraction happens on
                            QuoteMatrix&apos;s servers.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            7. Third parties
                        </h2>
                        <p>
                            To fetch product pages and estimate prices,
                            QuoteMatrix&apos;s server uses the following
                            processors, each bound by their own privacy terms:
                        </p>
                        <ul className="mt-3 space-y-2 list-disc pl-6">
                            <li>
                                <strong>Firecrawl</strong> — public web-page
                                fetching (
                                <a
                                    href="https://www.firecrawl.dev/privacy-policy"
                                    className="underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    firecrawl.dev privacy
                                </a>
                                )
                            </li>
                            <li>
                                <strong>Anthropic Claude</strong> — AI parsing
                                of extracted page text (
                                <a
                                    href="https://www.anthropic.com/legal/privacy"
                                    className="underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    anthropic.com privacy
                                </a>
                                )
                            </li>
                            <li>
                                <strong>17TRACK</strong> — carrier tracking
                                lookups (only for tracking numbers you enter, not
                                relevant to the extension)
                            </li>
                        </ul>
                        <p className="mt-3">
                            Only the product-page URL you sent is passed to
                            these processors. No personally identifiable
                            information about you is included.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            8. Data retention and deletion
                        </h2>
                        <p>
                            Product-page URLs you send may be cached briefly
                            (up to 24 hours) to speed up repeated look-ups; they
                            are not tied to your identity.
                        </p>
                        <p className="mt-3">
                            If you also submit a phone number to our giveaway
                            entry (a separate optional feature on our website,
                            not part of the extension), we store that number so
                            we can contact you if you win. You may request
                            deletion at any time by emailing us — see below.
                        </p>
                        <p className="mt-3">
                            <strong>To request deletion</strong> of any
                            information you have submitted, email{" "}
                            <a
                                href={`mailto:${CONTACT_EMAIL}?subject=Data%20deletion%20request`}
                                className="underline font-bold"
                            >
                                {CONTACT_EMAIL}
                            </a>{" "}
                            from the address (or including the phone number)
                            you used to submit the data. We will confirm
                            deletion within 30 days.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            9. Security
                        </h2>
                        <p>
                            All traffic between the extension, your browser and
                            QuoteMatrix is transmitted over encrypted HTTPS
                            connections. Access to the underlying data is
                            restricted to authorised QuoteMatrix administrators.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            10. Changes to this policy
                        </h2>
                        <p>
                            We may update this policy from time to time. Any
                            change will be reflected by an updated
                            &quot;Effective date&quot; at the top of this page.
                            Material changes will be announced on
                            quotematrix.online.
                        </p>
                    </section>

                    <section id="contact">
                        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-3">
                            11. Contact
                        </h2>
                        <p>
                            For any privacy question, complaint, or deletion
                            request, contact us at:
                        </p>
                        <div className="mt-3 border-2 border-zinc-900 bg-[#FFCC00] p-4 inline-block">
                            <div className="text-[10px] uppercase tracking-[0.25em] font-bold">
                                Email
                            </div>
                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                className="font-mono-tab text-lg font-extrabold text-zinc-900 hover:underline"
                                data-testid="privacy-contact-email"
                            >
                                {CONTACT_EMAIL}
                            </a>
                        </div>
                    </section>
                </div>

                <div className="mt-16 pt-8 border-t-2 border-dashed border-zinc-300 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400">
                    © {new Date().getFullYear()} QuoteMatrix
                </div>
            </main>
        </div>
    );
}
